import { useCallback, useState } from 'react';
import { cn } from '../cn.js';

/**
 * Bloc de commande copiable.
 *
 * Le cahier des charges impose des commandes copiables dans les plans de
 * réaction (§12.2) : c'est le geste qui permet à un administrateur non
 * spécialiste d'exécuter une étape sans risque de faute de frappe.
 *
 * `navigator.clipboard` n'est disponible qu'en contexte sécurisé (HTTPS ou
 * localhost). Le bouton signale explicitement l'échec plutôt que de laisser
 * croire que la copie a fonctionné.
 */
export function CommandBlock({
  command,
  shell,
  className,
}: {
  command: string;
  shell?: string | null;
  className?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'error'>('idle');

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command);
      setState('copied');
    } catch {
      setState('error');
    }
    setTimeout(() => setState('idle'), 2000);
  }, [command]);

  return (
    <div
      className={cn(
        'group relative rounded-lg border border-border-subtle bg-surface-sunken',
        className,
      )}
    >
      {shell ? (
        <span className="absolute left-3 top-2 font-mono text-[0.625rem] uppercase tracking-widest text-text-dim">
          {shell}
        </span>
      ) : null}
      <pre className={cn('overflow-x-auto px-3 pb-3 text-xs leading-relaxed', shell ? 'pt-7' : 'pt-3')}>
        <code className="text-brand-300">{command}</code>
      </pre>
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 rounded px-2 py-1 text-[0.625rem] font-medium uppercase tracking-wide text-text-dim ring-1 ring-border-strong transition-colors hover:bg-surface-raised hover:text-text"
      >
        {state === 'copied' ? 'Copié' : state === 'error' ? 'Échec' : 'Copier'}
      </button>
    </div>
  );
}

/** Log brut Wazuh, JSON indenté et défilable. */
export function JsonBlock({ value, maxHeight = '24rem' }: { value: unknown; maxHeight?: string }) {
  return (
    <pre
      className="overflow-auto rounded-lg border border-border-subtle bg-void px-4 py-3 text-xs leading-relaxed text-brand-300"
      style={{ maxHeight }}
    >
      <code>{JSON.stringify(value, null, 2)}</code>
    </pre>
  );
}
